import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import API from "../lib/api";
import { Save, Loader2, Wifi, CheckCircle, XCircle, Zap } from "lucide-react";
import { ConnectionsCard } from "../components/connections/ConnectionsLayout";
import { PageLayout } from "../components/PageLayout";

export default function ModelConfig() {
  const [config, setConfig] = useState({
    embedding_model: "text-embedding-3-small",
    reasoning_model: "gpt-4o",
    api_key_stored: "",
    temperature: 0.3,
    json_enforcement: true,
    tool_calling: true,
    use_local_model: false,
    local_model_url: "",
    local_model_name: "zentis-auditos-7b",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    API.get("/model-config")
      .then(({ data }) => setConfig(prev => ({ ...prev, ...data })))
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put("/model-config", config);
      toast.success("Model configuration saved");
      setTestResult(null);
    } catch (e) {
      toast.error("Failed to save");
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await API.post("/model-config/test");
      setTestResult(data);
      if (data.success) {
        toast.success(`Connected to ${data.model} — response: "${data.response}"`);
      } else {
        toast.error(`Connection failed: ${data.error}`);
      }
    } catch (e) {
      setTestResult({ success: false, error: e.message });
      toast.error("Connection test failed");
    }
    setTesting(false);
  };

  const isLocalActive = config.use_local_model && config.local_model_url;

  return (
    <PageLayout icon={Zap} title="Model Configuration" subtitle="Configure AI models, API keys, and inference parameters." data-testid="model-config-page">

      {/* Active model banner */}
      {isLocalActive && (
        <div className="rounded-md bg-green-50 border border-green-200 p-4 flex items-start gap-3">
          <Zap className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-800">
              Zentis AuditOS-7B Active
            </p>
            <p className="text-xs text-green-700 mt-0.5">
              Inference routing to your proprietary model at {config.local_model_url}
            </p>
            <div className="flex gap-2 mt-2 flex-wrap">
              {["100% JSON validity", "100% schema compliance", "Zero hallucinations"].map(m => (
                <span key={m} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                  {m}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Model Selection */}
      <ConnectionsCard title="Model Selection" contentClassName="space-y-4">
          <div>
            <Label className="text-xs">Reasoning Model</Label>
            <Select
              value={config.reasoning_model}
              onValueChange={v => setConfig(p => ({ ...p, reasoning_model: v }))}
            >
              <SelectTrigger className="mt-1 font-mono" data-testid="select-reasoning-model">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="zentis-auditos-7b">
                  ✦ Zentis AuditOS-7B (Local)
                </SelectItem>
                <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                <SelectItem value="gpt-4.1">GPT-4.1</SelectItem>
                <SelectItem value="gpt-4.1-mini">GPT-4.1 Mini</SelectItem>
                <SelectItem value="o3">O3</SelectItem>
                <SelectItem value="o4-mini">O4 Mini</SelectItem>
                <SelectItem value="openai/gpt-oss-120b">GPT-OSS 120B</SelectItem>
              </SelectContent>
            </Select>
            {config.reasoning_model === "zentis-auditos-7b" && !config.use_local_model && (
              <p className="text-xs text-amber-600 mt-1">
                ⚠ Enable "Use Zentis AuditOS-7B" below to route to the local server.
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs">Embedding Model</Label>
            <Select
              value={config.embedding_model}
              onValueChange={v => setConfig(p => ({ ...p, embedding_model: v }))}
            >
              <SelectTrigger className="mt-1 font-mono" data-testid="select-embedding-model">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text-embedding-3-small">text-embedding-3-small</SelectItem>
                <SelectItem value="text-embedding-3-large">text-embedding-3-large</SelectItem>
                <SelectItem value="text-embedding-ada-002">text-embedding-ada-002</SelectItem>
              </SelectContent>
            </Select>
          </div>
      </ConnectionsCard>

      {/* Zentis AuditOS-7B Local Model */}
      <ConnectionsCard
        title={(
          <span className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-purple-600" />
            Zentis AuditOS-7B (Remote GPU)
          </span>
        )}
        actions={isLocalActive && (
          <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50">
            Active
          </Badge>
        )}
        className={isLocalActive ? "border-green-300 bg-green-50/30" : ""}
        contentClassName="space-y-4"
      >
          {/* Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Use Zentis AuditOS-7B</p>
              <p className="text-xs text-muted-foreground">
                Route all inference to the fine-tuned model on the L40 GPU server
              </p>
            </div>
            <Switch
              checked={config.use_local_model}
              onCheckedChange={v => setConfig(p => ({ ...p, use_local_model: v }))}
            />
          </div>

          {config.use_local_model && (
            <>
              {/* URL */}
              <div>
                <Label className="text-xs">Model Server URL</Label>
                <Input
                  value={config.local_model_url}
                  onChange={e => setConfig(p => ({ ...p, local_model_url: e.target.value }))}
                  placeholder="http://45.198.14.45:8001/v1"
                  className="mt-1 font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  vLLM OpenAI-compatible endpoint on the L40 server. Include /v1 at the end.
                </p>
              </div>

              {/* Model name */}
              <div>
                <Label className="text-xs">Model Name</Label>
                <Input
                  value={config.local_model_name}
                  onChange={e => setConfig(p => ({ ...p, local_model_name: e.target.value }))}
                  placeholder="zentis-auditos-7b"
                  className="mt-1 font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Must match the --served-model-name used when starting vLLM.
                </p>
              </div>

              {/* Test connection */}
              <div className="pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTest}
                  disabled={testing || !config.local_model_url}
                  className="w-full"
                >
                  {testing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Wifi className="w-4 h-4 mr-2" />
                  )}
                  {testing ? "Testing connection..." : "Test Connection"}
                </Button>

                {/* Test result */}
                {testResult && (
                  <div className={`mt-2 rounded-md p-3 text-xs ${
                    testResult.success
                      ? "bg-green-50 border border-green-200 text-green-800"
                      : "bg-red-50 border border-red-200 text-red-800"
                  }`}>
                    {testResult.success ? (
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Connected successfully</p>
                          <p className="mt-0.5">Model: {testResult.model}</p>
                          <p>Response: "{testResult.response}"</p>
                          <p>Mode: {testResult.mode}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Connection failed</p>
                          <p className="mt-0.5">{testResult.error}</p>
                          <p className="mt-1 text-red-600">
                            Check that vLLM is running on the server and port 8001 is accessible.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
      </ConnectionsCard>

      {/* API Configuration */}
      <ConnectionsCard title="OpenAI Configuration" contentClassName="space-y-4">
          <div>
            <Label className="text-xs">API Key (Optional Override)</Label>
            <Input
              type="password"
              value={config.api_key_stored}
              onChange={e => setConfig(p => ({ ...p, api_key_stored: e.target.value }))}
              placeholder="sk-..."
              className="mt-1 font-mono"
              data-testid="api-key-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Leave blank to use OPENAI_API_KEY environment variable.
              Not required when using Zentis AuditOS-7B.
            </p>
          </div>
          <div>
            <Label className="text-xs">Temperature: {config.temperature}</Label>
            <Slider
              value={[config.temperature]}
              onValueChange={v => setConfig(p => ({ ...p, temperature: v[0] }))}
              min={0}
              max={1}
              step={0.1}
              className="mt-2"
              data-testid="temperature-slider"
            />
          </div>
      </ConnectionsCard>

      {/* Inference Settings */}
      <ConnectionsCard title="Inference Settings" contentClassName="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">JSON Enforcement</p>
              <p className="text-xs text-muted-foreground">
                Force structured JSON output from model
              </p>
            </div>
            <Switch
              checked={config.json_enforcement}
              onCheckedChange={v => setConfig(p => ({ ...p, json_enforcement: v }))}
              data-testid="json-enforcement-switch"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Tool Calling</p>
              <p className="text-xs text-muted-foreground">
                Enable function/tool calling capabilities
              </p>
            </div>
            <Switch
              checked={config.tool_calling}
              onCheckedChange={v => setConfig(p => ({ ...p, tool_calling: v }))}
              data-testid="tool-calling-switch"
            />
          </div>
      </ConnectionsCard>

      {/* Save */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={handleTest} disabled={testing}>
          {testing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Wifi className="w-4 h-4 mr-2" />
          )}
          Test Connection
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-config-btn"
          className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-10 px-5 shadow-md shadow-emerald-600/30 active:scale-95 border-0"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Configuration
        </Button>
      </div>
    </PageLayout>
  );
}




// import { useState, useEffect } from "react";
// import { useAudit } from "../context/AuditContext";
// import { Button } from "../components/ui/button";
// import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
// import { Input } from "../components/ui/input";
// import { Label } from "../components/ui/label";
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
// import { Switch } from "../components/ui/switch";
// import { Slider } from "../components/ui/slider";
// import { toast } from "sonner";
// import API from "../lib/api";
// import { Settings, Save, Loader2 } from "lucide-react";

// export default function ModelConfig() {
//   const [config, setConfig] = useState({
//     embedding_model: "text-embedding-3-small",
//     reasoning_model: "gpt-4o",
//     api_key_stored: "",
//     temperature: 0.3,
//     json_enforcement: true,
//     tool_calling: true
//   });
//   const [saving, setSaving] = useState(false);

//   useEffect(() => {
//     API.get("/model-config").then(({ data }) => setConfig(prev => ({ ...prev, ...data }))).catch(() => {});
//   }, []);

//   const handleSave = async () => {
//     setSaving(true);
//     try {
//       await API.put("/model-config", config);
//       toast.success("Model configuration saved");
//     } catch (e) { toast.error("Failed to save"); }
//     setSaving(false);
//   };

//   return (
//     <div className="space-y-6 max-w-2xl" data-testid="model-config-page">
//       <div>
//         <h2 className="text-xl font-semibold tracking-tight">Model Configuration</h2>
//         <p className="text-sm text-muted-foreground mt-1">Configure AI models, API keys, and inference parameters.</p>
//       </div>

//       <Card>
//         <CardHeader><CardTitle className="text-base">Model Selection</CardTitle></CardHeader>
//         <CardContent className="space-y-4">
//           <div>
//             <Label className="text-xs">Reasoning Model</Label>
//             <Select value={config.reasoning_model} onValueChange={v => setConfig(p => ({...p, reasoning_model: v}))}>
//               <SelectTrigger className="mt-1 font-mono" data-testid="select-reasoning-model"><SelectValue /></SelectTrigger>
//               <SelectContent>
//                 <SelectItem value="gpt-5.2">GPT-5.2</SelectItem>
//                 <SelectItem value="gpt-4o">GPT-4o</SelectItem>
//                 <SelectItem value="gpt-4.1">GPT-4.1</SelectItem>
//                 <SelectItem value="gpt-4.1-mini">GPT-4.1 Mini</SelectItem>
//                 <SelectItem value="o3">O3</SelectItem>
//                 <SelectItem value="o4-mini">O4 Mini</SelectItem>
//               </SelectContent>
//             </Select>
//           </div>
//           <div>
//             <Label className="text-xs">Embedding Model</Label>
//             <Select value={config.embedding_model} onValueChange={v => setConfig(p => ({...p, embedding_model: v}))}>
//               <SelectTrigger className="mt-1 font-mono" data-testid="select-embedding-model"><SelectValue /></SelectTrigger>
//               <SelectContent>
//                 <SelectItem value="text-embedding-3-small">text-embedding-3-small</SelectItem>
//                 <SelectItem value="text-embedding-3-large">text-embedding-3-large</SelectItem>
//                 <SelectItem value="text-embedding-ada-002">text-embedding-ada-002</SelectItem>
//               </SelectContent>
//             </Select>
//           </div>
//         </CardContent>
//       </Card>

//       <Card>
//         <CardHeader><CardTitle className="text-base">API Configuration</CardTitle></CardHeader>
//         <CardContent className="space-y-4">
//           <div>
//             <Label className="text-xs">API Key (Optional Override)</Label>
//             <Input
//               type="password"
//               value={config.api_key_stored}
//               onChange={e => setConfig(p => ({...p, api_key_stored: e.target.value}))}
//               placeholder="sk-..."
//               className="mt-1 font-mono"
//               data-testid="api-key-input"
//             />
//             <p className="text-xs text-muted-foreground mt-1">Leave blank to use environment variable.</p>
//           </div>
//           <div>
//             <Label className="text-xs">Temperature: {config.temperature}</Label>
//             <Slider
//               value={[config.temperature]}
//               onValueChange={v => setConfig(p => ({...p, temperature: v[0]}))}
//               min={0} max={1} step={0.1}
//               className="mt-2"
//               data-testid="temperature-slider"
//             />
//           </div>
//         </CardContent>
//       </Card>

//       <Card>
//         <CardHeader><CardTitle className="text-base">Inference Settings</CardTitle></CardHeader>
//         <CardContent className="space-y-4">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm font-medium">JSON Enforcement</p>
//               <p className="text-xs text-muted-foreground">Force structured JSON output from model</p>
//             </div>
//             <Switch checked={config.json_enforcement} onCheckedChange={v => setConfig(p => ({...p, json_enforcement: v}))} data-testid="json-enforcement-switch" />
//           </div>
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm font-medium">Tool Calling</p>
//               <p className="text-xs text-muted-foreground">Enable function/tool calling capabilities</p>
//             </div>
//             <Switch checked={config.tool_calling} onCheckedChange={v => setConfig(p => ({...p, tool_calling: v}))} data-testid="tool-calling-switch" />
//           </div>
//         </CardContent>
//       </Card>

//       <div className="flex justify-end">
//         <Button onClick={handleSave} disabled={saving} data-testid="save-config-btn">
//           {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
//           Save Configuration
//         </Button>
//       </div>
//     </div>
//   );
// }
